'use client';
/**
 * R14b — ?r= 를 «수화 뒤» 에 읽는 클라이언트 훅 + 칩→섬 알림.
 *
 * ⛔ useSearchParams 를 쓰지 않는다. 정적(ISR) 페이지에서 쓰면 Suspense 경계 밖 CSR 강등·빌드 경고가 나고,
 *    서버 페이지가 searchParams 를 await 하면 ƒ 가 된다. location.search 를 useEffect 안에서만 읽는다.
 *    → 서버 HTML·첫 수화 렌더는 언제나 「전국」(undefined → null 취급).
 *
 * 칩이 같은 경로에서 ?r= 만 바꿔 router.push 하면 경로가 같아 섬이 다시 마운트되지 않는다.
 * 그래서 칩은 적용 직후 `kd:region-param` 이벤트(detail.code)를 쏘고, 섬은 그것과 popstate 를 듣는다.
 */
import { useEffect, useState } from 'react';
import { readRegionParam } from './region-scope';

export const REGION_PARAM_EVENT = 'kd:region-param';

export function emitRegionParam(code: string | null): void {
  try { window.dispatchEvent(new CustomEvent(REGION_PARAM_EVENT, { detail: { code } })); } catch { /* 무시 */ }
}

/** undefined = 아직 수화 전(서버와 같은 「전국」 렌더를 유지), null = 파라미터 없음, string = ?r= 원문. */
export function useRegionParam(): string | null | undefined {
  const [code, setCode] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    const sync = () => setCode(readRegionParam(window.location.search));
    const onEmit = (e: Event) => {
      const d = (e as CustomEvent<{ code?: string | null }>).detail;
      setCode(d && typeof d.code === 'string' ? d.code : null);
    };
    sync();
    window.addEventListener('popstate', sync);
    window.addEventListener(REGION_PARAM_EVENT, onEmit);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(REGION_PARAM_EVENT, onEmit);
    };
  }, []);
  return code;
}

/**
 * ?r= 가 붙은 화면 → robots 를 noindex,follow 로(수화 뒤 head 주입). 파라미터가 빠지면 원래 값으로 되돌린다.
 * canonical 은 서버 메타가 이미 파라미터 없는 경로로 낸다 — 여기서는 없을 때만 보탠다.
 */
export function applyNoindex(on: boolean, canonicalPath: string | null): void {
  if (typeof document === 'undefined') return;
  const head = document.head;
  let meta = head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (on) {
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; head.appendChild(meta); meta.dataset.kdCreated = '1'; }
    if (meta.dataset.kdOrig === undefined) meta.dataset.kdOrig = meta.content || '';
    meta.content = 'noindex, follow';
    if (canonicalPath && !head.querySelector('link[rel="canonical"]')) {
      const l = document.createElement('link');
      l.rel = 'canonical';
      l.href = new URL(canonicalPath, window.location.origin).toString();
      l.dataset.kdCreated = '1';
      head.appendChild(l);
    }
  } else if (meta && meta.dataset.kdOrig !== undefined) {
    if (meta.dataset.kdCreated) meta.remove();
    else { meta.content = meta.dataset.kdOrig; delete meta.dataset.kdOrig; }
  }
}
